"use client";

import React, { useCallback } from "react";
import {
  ChevronsDownUp,
  ChevronsUpDown,
  ChevronDown,
  ChevronRight,
  Layers,
} from "lucide-react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import type { GroupingContext } from "@/components/shared/GroupingWrapper/types";

interface GroupHeaderContextMenuProps {
  groupingContext?: GroupingContext;
  /** Whether THIS group is currently expanded. */
  isExpanded: boolean;
  /** Toggle THIS group (already resolves single vs nested in GroupHeaderRow). */
  onToggle: () => void;
  /** The group-header row — cloned as the right-click trigger. */
  children: React.ReactElement;
}

/**
 * Right-click a group header to toggle it, expand/collapse every group at the
 * active level, or clear grouping — without hunting for the toolbar controls.
 * Renders the header untouched when there's no grouping context.
 */
export function GroupHeaderContextMenu({
  groupingContext,
  isExpanded,
  onToggle,
  children,
}: GroupHeaderContextMenuProps) {
  const isDual = Boolean(
    groupingContext?.dualGrouping?.primary && groupingContext?.dualGrouping?.secondary
  );

  const handleClearGrouping = useCallback(() => {
    if (isDual) {
      groupingContext?.setDualGrouping?.(null, null);
    } else {
      groupingContext?.setGroupByField?.(null);
    }
  }, [isDual, groupingContext]);

  if (!groupingContext) {
    return children;
  }

  const canClearGrouping = Boolean(
    groupingContext.setGroupByField || groupingContext.setDualGrouping
  );

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent className="w-52">
        <ContextMenuItem onSelect={onToggle}>
          {isExpanded ? (
            <ChevronRight className="mr-2 h-4 w-4" />
          ) : (
            <ChevronDown className="mr-2 h-4 w-4" />
          )}
          {isExpanded ? "Collapse group" : "Expand group"}
        </ContextMenuItem>

        <ContextMenuSeparator />

        <ContextMenuItem
          disabled={!groupingContext.onExpandAllGroups}
          onSelect={() => groupingContext.onExpandAllGroups?.()}
        >
          <ChevronsUpDown className="mr-2 h-4 w-4" />
          Expand all groups
        </ContextMenuItem>
        <ContextMenuItem
          disabled={!groupingContext.onCollapseAllGroups}
          onSelect={() => groupingContext.onCollapseAllGroups?.()}
        >
          <ChevronsDownUp className="mr-2 h-4 w-4" />
          Collapse all groups
        </ContextMenuItem>

        {canClearGrouping && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem onSelect={handleClearGrouping}>
              <Layers className="mr-2 h-4 w-4" />
              Clear grouping
            </ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}
